import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { ProgressProvider } from "@/contexts/ProgressContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import PublicLandingPage from "@/pages/PublicLandingPage";
import SignInPage from "@/pages/SignInPage";
import RegisterPage from "@/pages/RegisterPage";
import Dashboard from "@/pages/Dashboard";
import CategoryDetail from "@/pages/CategoryDetail";
import PricingPage from "@/pages/PricingPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import StarterLandingPage from "@/pages/StarterLandingPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { RedirectKeepQuery } from "@/components/RedirectKeepQuery";
import StorePage from "@/pages/StorePage";
import StoreProductPage from "@/pages/StoreProductPage";
import { CookieBanner } from "@/components/CookieBanner";
import { ExitIntentModal } from "@/components/ExitIntentModal";
import { CommandPalette } from "@/components/CommandPalette";
import { LifetimeDeadlineBanner } from "@/components/LifetimeDeadlineBanner";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { AdminMobileNav } from "@/components/AdminMobileNav";
import { RouteSeo } from "@/components/RouteSeo";

// Secondary + admin routes load on demand so the first mobile paint ships only the core journey.
const EliteTrackPage = lazy(() => import("@/pages/EliteTrackPage"));
const FavoritesPage = lazy(() => import("@/pages/FavoritesPage"));
const OGPreviewPage = lazy(() => import("@/pages/OGPreviewPage"));
const OGPreviewTrackPage = lazy(() => import("@/pages/OGPreviewTrackPage"));
const OnboardingQuiz = lazy(() => import("@/pages/OnboardingQuiz"));
const StarterPackPage = lazy(() => import("@/pages/StarterPackPage"));
const PublicTrackPreview = lazy(() => import("@/pages/PublicTrackPreview"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const ActivateAccountPage = lazy(() => import("@/pages/ActivateAccountPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const CancelSubscriptionPage = lazy(() => import("@/pages/CancelSubscriptionPage"));
const UpgradePage = lazy(() => import("@/pages/UpgradePage"));
const AdminUsersPage = lazy(() => import("@/pages/AdminUsersPage"));
const AdminCustomersPage = lazy(() => import("@/pages/AdminCustomersPage"));
const AdminLaunchpadPilotPage = lazy(() => import("@/pages/AdminLaunchpadPilotPage"));
const AdminRoadmapPage = lazy(() => import("@/pages/AdminRoadmapPage"));
const AdminCampaignsPage = lazy(() => import("@/pages/AdminCampaignsPage"));
const AdminTestimonialsPage = lazy(() => import("@/pages/AdminTestimonialsPage"));
const AdminDropsPage = lazy(() => import("@/pages/AdminDropsPage"));
const AdminHubPage = lazy(() => import("@/pages/AdminHubPage"));
const AdminSalesPage = lazy(() => import("@/pages/AdminSalesPage"));
const AdminRescueCheckoutsPage = lazy(() => import("@/pages/AdminRescueCheckoutsPage"));
const AdminStripeReconcilePage = lazy(() => import("@/pages/AdminStripeReconcilePage"));
const AdminManagementPage = lazy(() => import("@/pages/AdminManagementPage"));
const AdminAuditLogPage = lazy(() => import("@/pages/AdminAuditLogPage"));
const AdminInvitePage = lazy(() => import("@/pages/AdminInvitePage"));
const AdminDraftTracksPage = lazy(() => import("@/pages/AdminDraftTracksPage"));
const BuildYourOwnVaultPage = lazy(() => import("@/pages/BuildYourOwnVaultPage"));
const MyVaultPage = lazy(() => import("@/pages/MyVaultPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AdminContactPage = lazy(() => import("@/pages/AdminContactPage"));
const AdminAuditPage = lazy(() => import("@/pages/AdminAuditPage"));
const AuditPage = lazy(() => import("@/pages/AuditPage"));
const FirstOfferPage = lazy(() => import("@/pages/FirstOfferPage"));
const StartHerePage = lazy(() => import("@/pages/StartHerePage"));
const CommercialRoadmapPage = lazy(() => import("@/pages/CommercialRoadmapPage"));
const AdminVaultAuditPage = lazy(() => import("@/pages/AdminVaultAuditPage"));
const FirstOfferExamplePage = lazy(() => import("@/pages/FirstOfferExamplePage"));
const OfferSprintPage = lazy(() => import("@/pages/OfferSprintPage"));
const BuildsGalleryPage = lazy(() => import("@/pages/BuildsGalleryPage"));
const BuildSubmitPage = lazy(() => import("@/pages/BuildSubmitPage"));
const FriendReviewPage = lazy(() => import("@/pages/FriendReviewPage"));
const CreatorPackPage = lazy(() => import("@/pages/CreatorPackPage"));
const AdminBuildsPage = lazy(() => import("@/pages/AdminBuildsPage"));
const AdminCampaignPackPage = lazy(() => import("@/pages/AdminCampaignPackPage"));
const AdminByovPage = lazy(() => import("@/pages/AdminByovPage"));
const BlueprintSalesPage = lazy(() => import("@/pages/BlueprintSalesPage"));
const TemplateDetailPage = lazy(() => import("@/pages/TemplateDetailPage"));
const LibraryPreviewPage = lazy(() => import("@/pages/LibraryPreviewPage"));
const BundleLandingPage = lazy(() => import("@/pages/BundleLandingPage"));
const BundleBlogPage = lazy(() => import("@/pages/BundleBlogPage"));
const QuizPage = lazy(() => import("@/pages/QuizPage"));
const CampaignPage = lazy(() => import("@/pages/CampaignPage"));
const StudyIndexPage = lazy(() => import("@/pages/StudyIndexPage"));
const StudyArticlePage = lazy(() => import("@/pages/StudyArticlePage"));
const GuidesIndexPage = lazy(() => import("@/pages/GuidesIndexPage"));
const GuideArticlePage = lazy(() => import("@/pages/GuideArticlePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ShareQuickStartPage = lazy(() => import("@/pages/ShareQuickStartPage"));
const TikTokLaunchPage = lazy(() => import("@/pages/TikTokLaunchPage"));
const MilestoneLaunchPage = lazy(() => import("@/pages/MilestoneLaunchPage"));
const BetaLaunchPage = lazy(() => import("@/pages/BetaLaunchPage"));
const RedeemPage = lazy(() => import("@/pages/RedeemPage"));
const CohortPage = lazy(() => import("@/pages/CohortPage"));
const ChromeExtensionPage = lazy(() => import("@/pages/ChromeExtensionPage"));
const CollectionLandingPage = lazy(() => import("@/pages/CollectionLandingPage"));
const FastMonetisationServicesPage = lazy(() => import("@/pages/FastMonetisationServicesPage"));
const RoadmapPage = lazy(() => import("@/pages/RoadmapPage"));
const ThousandChallengePage = lazy(() => import("@/pages/ThousandChallengePage"));
const BirthdayCollectionPage = lazy(() => import("@/pages/BirthdayCollectionPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const SampleOutputsPage = lazy(() => import("@/pages/SampleOutputsPage"));
const AffiliatesPage = lazy(() => import("@/pages/AffiliatesPage"));
const PlaybookSalesPage = lazy(() => import("@/pages/PlaybookSalesPage"));
const WorkflowTrackPage = lazy(() => import("@/pages/WorkflowTrackPage"));
const PlaybookDownloadPage = lazy(() => import("@/pages/PlaybookDownloadPage"));
const AffiliateTermsPage = lazy(() => import("@/pages/AffiliateTermsPage"));
const AdminAffiliatesPage = lazy(() => import("@/pages/AdminAffiliatesPage"));
const MemberStoriesPage = lazy(() => import("@/pages/MemberStoriesPage"));
const EditorialMethodPage = lazy(() => import("@/pages/EditorialMethodPage"));
const AuthorPage = lazy(() => import("@/pages/AuthorPage"));
const ReviewCorrectionsPage = lazy(() => import("@/pages/ReviewCorrectionsPage"));
const FounderPage = lazy(() => import("@/pages/FounderPage"));
const ResultsDisclaimerPage = lazy(() => import("@/pages/FounderPage").then((m) => ({ default: m.ResultsDisclaimerPage })));
const AdminMemberStoriesPage = lazy(() => import("@/pages/AdminMemberStoriesPage"));
const AdminCorrectionsPage = lazy(() => import("@/pages/AdminTrustPages").then((m) => ({ default: m.AdminCorrectionsPage })));
const AdminContentDatesPage = lazy(() => import("@/pages/AdminTrustPages").then((m) => ({ default: m.AdminContentDatesPage })));
const AdminFoundersPickPage = lazy(() => import("@/pages/AdminFoundersPickPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const RefundPolicyPage = lazy(() => import("@/pages/RefundPolicyPage"));

const RouteFallback = () => <div className="min-h-[60vh]" aria-busy="true" data-testid="route-loading" />;

function App() {
  return (
    <div className="App bg-black min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <FavoritesProvider>
            <ProgressProvider>
              <AnalyticsTracker />
              <AdminMobileNav />
              <RouteSeo />
              <LifetimeDeadlineBanner />
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<PublicLandingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/roadmap" element={<RoadmapPage />} />
                <Route path="/1k-challenge" element={<ThousandChallengePage />} />
                <Route path="/share/quick-start/:slug" element={<ShareQuickStartPage />} />
                <Route path="/tiktok" element={<TikTokLaunchPage />} />
                <Route path="/1000" element={<MilestoneLaunchPage />} />
                <Route path="/beta" element={<BetaLaunchPage />} />
                <Route path="/redeem" element={<RedeemPage />} />
                <Route path="/blueprint" element={<BlueprintSalesPage />} />
                <Route path="/library-preview" element={<LibraryPreviewPage />} />
                <Route path="/bundle/:key" element={<BundleLandingPage />} />
                <Route path="/bundles/:key" element={<BundleLandingPage />} />
                <Route path="/blog/best-ai-prompts/:slug" element={<BundleBlogPage />} />
                <Route path="/quiz" element={<QuizPage />} />
                <Route path="/collection/birthday" element={<BirthdayCollectionPage />} />
                <Route path="/collection/:key" element={<CollectionLandingPage />} />
                <Route path="/collections/fast-monetisation-services" element={<FastMonetisationServicesPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/affiliates" element={<AffiliatesPage />} />
                <Route path="/lifetime" element={<RedirectKeepQuery to="/pricing" />} />
                <Route path="/youtube-clipping" element={<RedirectKeepQuery to="/store/youtube-clipping-rights-cleared-shorts" />} />
                <Route path="/operator-power-prompts" element={<RedirectKeepQuery to="/store/operator-power-prompts" />} />
                <Route path="/startup-launch-partner" element={<RedirectKeepQuery to="/store/startup-launch-partner" />} />
                <Route path="/playbook" element={<PlaybookSalesPage />} />
                <Route path="/store" element={<StorePage />} />
                <Route path="/build-your-own-vault" element={<BuildYourOwnVaultPage />} />
                <Route path="/my-vault" element={<MyVaultPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/admin/contact" element={<AdminContactPage />} />
                <Route path="/admin/audit" element={<AdminAuditPage />} />
                <Route path="/audit" element={<AuditPage />} />
                <Route path="/first-offer" element={<FirstOfferPage />} />
                <Route path="/start-here" element={<StartHerePage />} />
                <Route path="/commercial-roadmap" element={<CommercialRoadmapPage />} />
                <Route path="/admin/vault-audit" element={<ProtectedRoute><AdminVaultAuditPage /></ProtectedRoute>} />
                <Route path="/first-offer/example" element={<FirstOfferExamplePage />} />
                <Route path="/offer-sprint" element={<OfferSprintPage />} />
                <Route path="/builds" element={<BuildsGalleryPage />} />
                <Route path="/builds/submit" element={<BuildSubmitPage />} />
                <Route path="/review/:token" element={<FriendReviewPage />} />
                <Route path="/creator-pack" element={<CreatorPackPage />} />
                <Route path="/admin/builds" element={<ProtectedRoute><AdminBuildsPage /></ProtectedRoute>} />
                <Route path="/admin/campaign-pack" element={<ProtectedRoute><AdminCampaignPackPage /></ProtectedRoute>} />
                <Route path="/admin/byov" element={<ProtectedRoute><AdminByovPage /></ProtectedRoute>} />
                <Route path="/store/:slug" element={<StoreProductPage />} />
                <Route path="/playbook/download" element={<PlaybookDownloadPage />} />
                <Route path="/affiliate-terms" element={<AffiliateTermsPage />} />
                <Route path="/admin/affiliates" element={<ProtectedRoute><AdminAffiliatesPage /></ProtectedRoute>} />
                <Route path="/sample-outputs" element={<SampleOutputsPage />} />
                <Route path="/sample-outputs/:id" element={<SampleOutputsPage />} />
                <Route path="/member-stories" element={<MemberStoriesPage />} />
                <Route path="/editorial-method" element={<EditorialMethodPage />} />
                <Route path="/authors/ross" element={<AuthorPage />} />
                <Route path="/review-and-corrections" element={<ReviewCorrectionsPage />} />
                <Route path="/founder" element={<FounderPage />} />
                <Route path="/results-disclaimer" element={<ResultsDisclaimerPage />} />
                <Route path="/admin/member-stories" element={<ProtectedRoute><AdminMemberStoriesPage /></ProtectedRoute>} />
                <Route path="/admin/corrections" element={<ProtectedRoute><AdminCorrectionsPage /></ProtectedRoute>} />
                <Route path="/admin/content-dates" element={<ProtectedRoute><AdminContentDatesPage /></ProtectedRoute>} />
                <Route path="/admin/founders-pick" element={<ProtectedRoute><AdminFoundersPickPage /></ProtectedRoute>} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/refund-policy" element={<RefundPolicyPage />} />
                <Route path="/sign-in" element={<SignInPage />} />
                <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
                <Route path="/auth/reset" element={<ResetPasswordPage />} />
                <Route path="/auth/activate" element={<ActivateAccountPage />} />
                <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
                <Route path="/initiate" element={<RegisterPage />} />
                <Route path="/initiate/welcome" element={<ProtectedRoute><OnboardingQuiz /></ProtectedRoute>} />
                <Route path="/og-preview" element={<OGPreviewPage />} />
                <Route path="/og-preview/track/:slug" element={<OGPreviewTrackPage />} />
                <Route path="/p/:slug" element={<PublicTrackPreview />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/starter-pack" element={<StarterPackPage />} />
                <Route path="/starter" element={<StarterLandingPage />} />
                {/* Retired: Founder's Circle tier — redirect any old bookmarks to /pricing. */}
                <Route path="/founders-circle" element={<Navigate to="/pricing" replace />} />
                <Route path="/billing/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
                <Route path="/billing/cancel" element={<ProtectedRoute><CancelSubscriptionPage /></ProtectedRoute>} />
                <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><AdminHubPage /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
                <Route path="/admin/customers" element={<ProtectedRoute><AdminCustomersPage /></ProtectedRoute>} />
                <Route path="/admin/launchpad-pilot" element={<ProtectedRoute><AdminLaunchpadPilotPage /></ProtectedRoute>} />
                <Route path="/admin/roadmap" element={<ProtectedRoute><AdminRoadmapPage /></ProtectedRoute>} />
                <Route path="/admin/campaigns" element={<ProtectedRoute><AdminCampaignsPage /></ProtectedRoute>} />
                <Route path="/admin/testimonials" element={<ProtectedRoute><AdminTestimonialsPage /></ProtectedRoute>} />
                <Route path="/admin/drops" element={<ProtectedRoute><AdminDropsPage /></ProtectedRoute>} />
                <Route path="/admin/sales" element={<ProtectedRoute><AdminSalesPage /></ProtectedRoute>} />
                <Route path="/admin/rescue-stuck-checkouts" element={<ProtectedRoute><AdminRescueCheckoutsPage /></ProtectedRoute>} />
                <Route path="/admin/stripe-reconcile" element={<ProtectedRoute><AdminStripeReconcilePage /></ProtectedRoute>} />
                <Route path="/admin/admins" element={<ProtectedRoute><AdminManagementPage /></ProtectedRoute>} />
                <Route path="/admin/audit-log" element={<ProtectedRoute><AdminAuditLogPage /></ProtectedRoute>} />
                <Route path="/admin/drafts" element={<ProtectedRoute><AdminDraftTracksPage /></ProtectedRoute>} />
                <Route path="/admin-invite" element={<AdminInvitePage />} />
                <Route path="/c/:slug" element={<CampaignPage />} />
                <Route path="/study" element={<StudyIndexPage />} />
                <Route path="/study/:slug" element={<StudyArticlePage />} />
                <Route path="/guides" element={<GuidesIndexPage />} />
                <Route path="/guides/:slug" element={<GuideArticlePage />} />
                <Route path="/vault" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/vault/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
                <Route path="/vault/templates/:slug" element={<ProtectedRoute><TemplateDetailPage /></ProtectedRoute>} />
                <Route path="/vault/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
                <Route path="/vault/elite" element={<ProtectedRoute><EliteTrackPage /></ProtectedRoute>} />
                <Route path="/vault/ebook-builder" element={<ProtectedRoute><WorkflowTrackPage /></ProtectedRoute>} />
                <Route path="/vault/:slug" element={<ProtectedRoute><CategoryDetail /></ProtectedRoute>} />
                <Route path="/cohort" element={<ProtectedRoute><CohortPage /></ProtectedRoute>} />
                <Route path="/chrome-extension" element={<ChromeExtensionPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </Suspense>
              {/* Founder's gift launch: PriceSlashBanner retired from HQ per user
                  request — post-launch, urgency banner no longer needed. */}
              <ExitIntentModal />
              <CookieBanner />
              <CommandPalette />
              <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                  style: {
                    background: "#0a0a0a",
                    border: "1px solid rgba(168, 85, 247, 0.4)",
                    color: "#fff",
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: "12px",
                    borderRadius: 0,
                  },
                }}
              />
            </ProgressProvider>
          </FavoritesProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
